import { prefersReducedMotion, useReveal } from "../lib/useReveal";

const C = 230;
const R = 150;
const LATS = [-60, -30, 0, 30, 60];
const MERIDIANS = 6;

// Edge points (data centres) placed on the visible hemisphere, as fractions of R.
const POINTS: Array<[number, number]> = [
  [-0.46, -0.34],
  [0.2, -0.56],
  [0.56, -0.06],
  [0.34, 0.46],
  [-0.24, 0.54],
  [-0.62, 0.12],
  [0.06, 0.0],
];
const ARCS: Array<[number, number]> = [
  [0, 2],
  [1, 4],
  [5, 3],
  [6, 1],
  [2, 3],
];

export function EdgeGlobe(): React.ReactElement {
  const { ref, shown } = useReveal(0.25);
  const reduced = prefersReducedMotion();
  const pts = POINTS.map(([fx, fy]) => [C + fx * R, C + fy * R] as const);

  const arcs = ARCS.map(([a, b], index) => {
    const [x1, y1] = pts[a];
    const [x2, y2] = pts[b];
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = mx - C;
    const dy = my - C;
    const len = Math.hypot(dx, dy) || 1;
    const push = 74;
    const cx = mx + (dx / len) * push;
    const cy = my + (dy / len) * push;
    return { id: `edge-arc-${index}`, d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`, begin: index * 0.55 };
  });

  return (
    <div ref={ref} className={`viz viz-globe${shown ? " is-shown" : ""}`}>
      <svg viewBox="0 0 460 460" role="img" aria-label="Nevosoft builds connected systems delivered across a global edge network.">
        <defs>
          <radialGradient id="globe-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#3f86ff" stopOpacity="0.32" />
            <stop offset="1" stopColor="#3f86ff" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="globe-fill" cx="0.4" cy="0.34" r="0.75">
            <stop offset="0" stopColor="#143b80" />
            <stop offset="1" stopColor="#0a1f48" />
          </radialGradient>
        </defs>

        <circle cx={C} cy={C} r={R + 40} fill="url(#globe-glow)" />
        {!reduced && <circle cx={C} cy={C} r={R + 18} className="globe-orbit" />}
        <circle cx={C} cy={C} r={R} fill="url(#globe-fill)" />
        <circle cx={C} cy={C} r={R} className="globe-limb" />

        <g className="globe-grid">
          {LATS.map((lat) => {
            const rad = (lat * Math.PI) / 180;
            const rx = R * Math.cos(rad);
            const ry = Math.max(2, rx * 0.34);
            const cy = C - R * Math.sin(rad) * 0.92;
            return <ellipse key={`lat-${lat}`} cx={C} cy={cy} rx={rx} ry={ry} className="globe-line" />;
          })}
          {Array.from({ length: MERIDIANS }).map((_, k) => {
            const lon = ((k + 0.5) / MERIDIANS) * Math.PI;
            const rx = R * Math.abs(Math.sin(lon));
            return (
              <ellipse
                key={`mer-${k}`}
                cx={C}
                cy={C}
                rx={rx}
                ry={R * 0.985}
                className={`globe-line globe-meridian${reduced ? "" : " globe-meridian--spin"}`}
                style={reduced ? undefined : { animationDelay: `${(-16 * k) / MERIDIANS}s` }}
              />
            );
          })}
        </g>

        <g className="globe-arcs">
          {arcs.map((arc) => (
            <g key={arc.id}>
              <path id={arc.id} d={arc.d} className="globe-arc" />
              {shown && !reduced && (
                <circle r="3" className="globe-spark">
                  <animateMotion dur="2.8s" begin={`${arc.begin}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                    <mpath href={`#${arc.id}`} />
                  </animateMotion>
                </circle>
              )}
            </g>
          ))}
        </g>

        <g className="globe-points">
          {pts.map(([x, y], index) => (
            <g key={`pt-${index}`} className="globe-point" style={{ transitionDelay: `${260 + index * 70}ms` }}>
              {!reduced && <circle cx={x} cy={y} className="globe-point-ring" style={{ animationDelay: `${index * 0.3}s` }} />}
              <circle cx={x} cy={y} r="3.4" className="globe-point-core" />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
